import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Dialog, DialogContent } from "./ui/dialog";
import { useCart, REWARD_THRESHOLD } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, fmtCLP } from "../lib/api";
import { Plus, Minus, Gift, Trash2, CreditCard, Receipt, ExternalLink, X, Sparkles, Copy, Check } from "lucide-react";
import ReceiptForm from "./ReceiptForm";
import AuthDialog from "./AuthDialog";

export default function CartDrawer() {
  const { items, removeItem, updateQty, subtotal, hasGift, remaining, progress, open, setOpen, clear } = useCart();
  const { user } = useAuth();

  // Flow states
  const [registerPrompt, setRegisterPrompt] = useState(false);
  const [payInfo, setPayInfo] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [paymentLink, setPaymentLink] = useState("");

  const onPagar = async () => {
    // Load latest payment_link from settings
    try {
      const { data } = await api.get("/settings");
      setPaymentLink(data.payment_link || "");
    } catch {}
    if (!user) {
      setRegisterPrompt(true);
    } else {
      setPayInfo(true);
    }
  };

  const goToPayInfo = () => {
    setRegisterPrompt(false);
    setPayInfo(true);
  };

  const openPaymentLink = () => {
    if (paymentLink) {
      window.open(paymentLink, "_blank", "noopener,noreferrer");
    }
    setPayInfo(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
          <SheetHeader className="px-5 py-4 border-b">
            <SheetTitle className="font-heading text-2xl font-black">Tu carrito</SheetTitle>
          </SheetHeader>

          {/* Reward thermometer */}
          <div className="px-5 py-4 border-b bg-sky-50/40">
            {hasGift ? (
              <div className="text-sm font-bold text-emerald-700 flex items-center gap-2" data-testid="reward-unlocked">
                <Gift size={18} /> 🎉 ¡Felicidades! Ganaste un producto sorpresa
              </div>
            ) : (
              <div className="text-sm font-bold text-slate-700 flex items-center gap-2" data-testid="reward-progress-msg">
                <Gift size={18} className="text-[#0EA5E9]" />
                Te faltan <span className="text-[#0EA5E9]">{fmtCLP(remaining)}</span> para tu regalo sorpresa 🎁
              </div>
            )}
            <div className="thermo-track mt-3">
              <div className="thermo-fill" style={{ width: `${progress}%` }} data-testid="reward-progress-bar" />
            </div>
            <div className="flex justify-between mt-1.5 text-[11px] font-bold text-slate-500">
              <span>{fmtCLP(subtotal)}</span>
              <span>{fmtCLP(REWARD_THRESHOLD)}</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {items.length === 0 && (
              <div className="text-center py-16 text-slate-500" data-testid="empty-cart">
                <div className="text-5xl mb-3">🛒</div>
                Tu carrito está vacío
              </div>
            )}
            {items.map((it) => (
              <div key={it._lineId} className="flex gap-3 py-3 border-b last:border-b-0" data-testid={`cart-item-${it._lineId}`}>
                <div className="w-20 h-20 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0">
                  {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-sm leading-tight line-clamp-2">{it.name}</div>
                  {it.custom_text && <div className="text-xs text-slate-500">Texto: "{it.custom_text}"</div>}
                  <div className="font-heading font-black text-sm mt-1">{fmtCLP(it.price * it.quantity)}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="inline-flex items-center bg-slate-100 rounded-full">
                      <button onClick={() => updateQty(it._lineId, it.quantity - 1)} className="w-7 h-7 flex items-center justify-center" data-testid={`qty-minus-${it._lineId}`}>
                        <Minus size={12} />
                      </button>
                      <span className="text-sm font-bold w-6 text-center">{it.quantity}</span>
                      <button onClick={() => updateQty(it._lineId, it.quantity + 1)} className="w-7 h-7 flex items-center justify-center" data-testid={`qty-plus-${it._lineId}`}>
                        <Plus size={12} />
                      </button>
                    </div>
                    <button onClick={() => removeItem(it._lineId)} className="text-slate-400 hover:text-red-500" data-testid={`remove-${it._lineId}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {items.length > 0 && (
            <div className="border-t px-5 py-4 bg-white space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-bold">Subtotal</span>
                <span className="font-heading font-black text-2xl">{fmtCLP(subtotal)}</span>
              </div>
              {hasGift && (
                <div className="text-xs font-bold text-emerald-700 inline-flex items-center gap-1">
                  <Gift size={13} /> Incluye regalo sorpresa
                </div>
              )}
              <button
                onClick={onPagar}
                className="w-full btn-primary inline-flex items-center justify-center gap-2 text-base"
                data-testid="cart-pay-btn"
              >
                <CreditCard size={18} /> Pagar
              </button>
              <button
                onClick={() => setReceiptOpen(true)}
                className="w-full bg-black hover:bg-slate-800 text-white font-bold py-3 rounded-full inline-flex items-center justify-center gap-2 transition"
                data-testid="cart-upload-receipt-btn"
              >
                <Receipt size={18} /> Cargar comprobante
              </button>
              <button onClick={clear} className="w-full text-xs text-slate-500 hover:text-red-500 font-bold py-1" data-testid="clear-cart-btn">
                Vaciar carrito
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Register prompt */}
      <Dialog open={registerPrompt} onOpenChange={setRegisterPrompt}>
        <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="register-prompt-dialog">
          <div className="p-6">
            <div className="w-14 h-14 rounded-2xl bg-sky-100 text-[#0EA5E9] flex items-center justify-center mb-4">
              <Sparkles size={26} />
            </div>
            <h3 className="font-heading font-black text-2xl text-black">¿Quieres registrarte?</h3>
            <p className="text-slate-600 mt-2 leading-relaxed">
              Crea una cuenta gratis y recibirás <strong>promociones, novedades</strong> y descuentos exclusivos. También podrás guardar tus datos para tus próximas compras.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => { setRegisterPrompt(false); setAuthOpen(true); }}
                className="flex-1 btn-primary"
                data-testid="prompt-register-yes"
              >
                Sí, registrarme
              </button>
              <button
                onClick={goToPayInfo}
                className="flex-1 px-5 py-3 rounded-full bg-slate-100 hover:bg-slate-200 font-bold"
                data-testid="prompt-register-no"
              >
                No, continuar
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pay info dialog */}
      <Dialog open={payInfo} onOpenChange={setPayInfo}>
        <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="pay-info-dialog">
          <div className="p-6">
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mb-4">
              <Sparkles size={26} />
            </div>
            <h3 className="font-heading font-black text-2xl text-black">¡Ya casi son tuyas! 🚀</h3>
            <p className="text-slate-600 mt-2 leading-relaxed">
              Ahora te dirigimos al pago. <strong>Importante:</strong>
            </p>
            <ol className="mt-3 space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><span className="font-bold text-[#0EA5E9]">1.</span> Presiona el botón <strong>copiar</strong> para pegar el monto exacto al momento de pagar.</li>
              <li className="flex gap-2"><span className="font-bold text-[#0EA5E9]">2.</span> Toma una <strong>captura de pantalla</strong> del comprobante.</li>
              <li className="flex gap-2"><span className="font-bold text-[#0EA5E9]">3.</span> Al volver, presiona <strong>"Cargar comprobante"</strong> para adjuntarlo y enviar tus datos de envío.</li>
            </ol>

            <CopyAmount amount={subtotal} />

            <button
              onClick={openPaymentLink}
              disabled={!paymentLink}
              className="mt-4 w-full btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="goto-payment-btn"
            >
              <ExternalLink size={16} /> Ir al pago
            </button>
            {!paymentLink && <div className="text-xs text-amber-600 mt-2 text-center">El link de pago aún no está configurado por el administrador.</div>}
          </div>
        </DialogContent>
      </Dialog>

      <AuthDialog
        open={authOpen}
        defaultTab="register"
        onClose={() => setAuthOpen(false)}
        onSuccess={() => { setAuthOpen(false); setPayInfo(true); }}
      />

      <ReceiptForm
        open={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        onSuccess={() => { setReceiptOpen(false); setOpen(false); clear(); }}
      />
    </>
  );
}

const CopyAmount = ({ amount }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(amount));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  return (
    <div className="mt-4 p-4 rounded-xl bg-black text-white flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-widest font-bold text-slate-300">Monto</div>
        <div className="font-heading font-black text-2xl">{fmtCLP(amount)}</div>
      </div>
      <button onClick={onCopy} className="bg-[#0EA5E9] hover:bg-sky-400 text-white px-3.5 py-2 rounded-full font-bold inline-flex items-center gap-1.5 text-sm" data-testid="copy-amount-pay">
        {copied ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar</>}
      </button>
    </div>
  );
};
